import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../ui/Input';
import { fmtTime, receivingApi, type Journal } from '../../lib/receiving';

/** One audit detail, readable at a glance: "product: 0986479R72 · qty: 1 · van: INTERCARS". */
function summarise(detail: unknown): string {
  if (!detail || typeof detail !== 'object') return '';
  return Object.entries(detail as Record<string, unknown>)
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join(' · ')
    .slice(0, 220);
}

/**
 * The floor's Journal (receiving prototype, "Log"): the documents issued on the day and every
 * receiving action with the employee, the role and what it did. Server-filtered to the
 * receiving module's own actions (JournalService), so it is safe for floor roles to read.
 */
export function JournalView() {
  const { t } = useTranslation();
  const [day, setDay] = useState('');
  const [data, setData] = useState<Journal | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    receivingApi
      .journal(day || undefined)
      .then((j) => {
        setData(j);
        setError('');
      })
      .catch(() => setError(t('rcv.journal.error')));
  }, [day, t]);

  return (
    <div className="space-y-4 text-base">
      <div className="flex items-center gap-3">
        <label className="text-sm text-muted-foreground" htmlFor="journal-day">
          {t('rcv.journal.day')}
        </label>
        <Input
          id="journal-day"
          type="date"
          className="w-auto"
          value={day || data?.day || ''}
          onChange={(e) => setDay(e.target.value)}
        />
      </div>
      {error && <p className="text-destructive">{error}</p>}

      <section className="rounded-xl border bg-card p-4">
        <h2 className="mb-2 text-lg font-semibold">{t('rcv.journal.documents')}</h2>
        {data && data.documents.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('rcv.journal.noDocuments')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-1">{t('rcv.journal.time')}</th>
                <th>{t('rcv.journal.document')}</th>
                <th>{t('rcv.journal.customer')}</th>
                <th>{t('rcv.journal.by')}</th>
              </tr>
            </thead>
            <tbody>
              {data?.documents.map((d) => (
                <tr key={`${d.docType}:${d.id}`} className="border-t">
                  <td className="py-1.5">{fmtTime(d.date)}</td>
                  <td>
                    <span className="font-mono">{d.number}</span>{' '}
                    <span className="text-xs text-muted-foreground">
                      {t(`orders.documents.type.${d.docType}`, { defaultValue: d.docType })}
                    </span>
                  </td>
                  <td>{d.customerName ?? '—'}</td>
                  <td>{d.createdBy ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="rounded-xl border bg-card p-4">
        <h2 className="mb-2 text-lg font-semibold">{t('rcv.journal.actions')}</h2>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="py-1">{t('rcv.journal.time')}</th>
              <th>{t('rcv.journal.employee')}</th>
              <th>{t('rcv.journal.role')}</th>
              <th>{t('rcv.journal.action')}</th>
              <th>{t('rcv.journal.detail')}</th>
            </tr>
          </thead>
          <tbody>
            {data?.actions.map((a, i) => (
              <tr key={i} className="border-t align-top">
                <td className="py-1.5 whitespace-nowrap">{fmtTime(a.at)}</td>
                <td className="font-medium">{a.actor}</td>
                <td>{a.role ?? '—'}</td>
                <td className="whitespace-nowrap">{a.action}</td>
                <td className="text-xs text-muted-foreground">{summarise(a.detail)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.actions.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('rcv.journal.noActions')}</p>
        )}
      </section>
    </div>
  );
}
